import { useCallback, useEffect, useState } from "react";

import useEventCallback from "./useEventCallback";
import useEventListener from "./useEventListener";

// Persist the state with local storage so that it remains after a page refresh. This can be useful for a dark theme.
// This hook is used in the same way as useState except that you must pass the storage key in the 1st parameter.
// The 2nd parameter is the initial state and the return value is the state and a function to update the state.
/*  
  interface LocalStorageState<T> {
    key: string;
    initialValue: T | (() => T);
  }
  */
const IS_SERVER = typeof window === "undefined";

//   const useLocalStorage = <T>({ key, initialValue }: LocalStorageState<T>) =>

//Options: serializer and deserializer functions
// deserializer? (value: string) => T; A function to deserialize the stored value
// serializer? (value: T) => string; A function to serialize the value before storing it

/**
 * A custom hook that synchronizes a state variable with local storage.
 * @param {Object} param0 - The hook parameters
 * @param {string} param0.key - The local storage key
 * @param {any} param0.initialValue - The initial value to use if no value is found in local storage
 * @param {Object} [param0.options] - Optional configuration for serialization and deserialization
 * @param {function} [param0.options.serializer] - A function to serialize the value before storing
 * @param {function} [param0.options.deserializer] - A function to deserialize the stored value
 * @returns {[any, function]} - An array containing the current value and a function to update it
 *
 * @example
 * const [value, setValue] = useLocalStorage({
 *   key: 'myKey',
 *   initialValue: 'default value',
 *   options: {
 *     serializer: (value) => value.toString(),
 *     deserializer: (value) => value,
 *      initializeWithValue: true, // Optional, defaults to true
 *   },
 * });
 */

const useLocalStorage = ({ key, initialValue, options }) => {
  const { initializeWithValue = true } = options || {};
  const serializer = useCallback(
    (value) => {
      if (options?.serializer && typeof options.serializer === "function") {
        return options.serializer(value);
      }
      return JSON.stringify(value);
    },
    [options]
  );

  const deserializer = useCallback(
    (value) => {
      // Support 'undefined' as a value
      if (options?.deserializer && typeof options.deserializer === "function") {
        return options.deserializer(value);
      }

      if (value === "undefined") {
        return undefined;
      }

      const defaultValue =
        initialValue instanceof Function ? initialValue() : initialValue;

      let parsed;
      try {
        parsed = JSON.parse(value);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error parsing JSON:", error);
        return defaultValue; // Return initialValue if parsing fails
      }

      return parsed;
    },
    [initialValue, options]
  );

  /*
    const deserializer = useCallback<(value: string) => T>(
      (value) => {
        // Support 'undefined' as a value
        if (value === 'undefined') {
          return undefined as unknown as T;
        }
  
        const defaultValue =
          initialValue instanceof Function ? initialValue() : initialValue;
  
        let parsed: unknown;
        try {
          parsed = JSON.parse(value);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error parsing JSON:', error);
          return defaultValue; // Return initialValue if parsing fails
        }
  
        return parsed as T;
      },
      [initialValue],
    );
    */

  // Get from local storage then
  // parse stored json or return initialValue
  // const readValue = useCallback<() => T>(() => {
  const readValue = useCallback(() => {
    const initialValueToUse =
      initialValue instanceof Function ? initialValue() : initialValue;

    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      return initialValueToUse;
    }

    try {
      const raw = window.localStorage.getItem(key);
      return raw ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState(() => {
    if (initializeWithValue) {
      return readValue();
    }

    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  // const setValue = Dispatch<SetStateAction<T>>
  const setValue = useEventCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      // eslint-disable-next-line no-console
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client`
      );
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(readValue()) : value;

      // Save to local storage
      window.localStorage.setItem(key, serializer(newValue));

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every similar useLocalStorage hook is notified
      window.dispatchEvent(new StorageEvent("local-storage", { key }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  });

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /*
    const handleStorageChange = useCallback(
      (event: StorageEvent | CustomEvent) => {
        if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
          return;
        }
  
        setStoredValue(readValue());
      },
      [key, readValue],
    );
    */
  const handleStorageChange = useCallback(
    (event) => {
      if (event.key && event.key !== key) {
        return;
      }

      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // this only works for other documents, not the current one
  useEventListener("storage", handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  // See: useLocalStorage()
  useEventListener("local-storage", handleStorageChange);

  // return [storedValue, setValue] as const;
  return [storedValue, setValue];
};

export default useLocalStorage;
